import { QRCodeSVG } from "qrcode.react";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/Input";
import { ExtrasText } from "../style";
import { MaxButton } from "../../shared/NumericField";
import {
  InputContainer,
  InputLabel,
  QRSection,
  QRWrapper,
  AddressText,
  CopyButton,
  SliderContainer,
  SliderRow,
  SliderInput,
  PercentageLabel,
} from "./style";

import { DepositWithdrawCollaterals } from "../../../constants/addresses";
import { FIELD_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { ChainIdEnum } from "../../../enums/chain";
import { shortenAddress } from "../../../utils/strings";
import {
  isValidDecimalInput,
  toTokenUnits,
} from "../../../utils/decimalString";
import { invalidAmountMessage } from "../../DepositWithdrawModal/WithdrawModal.helpers";
import {
  getAssetLogo,
  getAssetShortName,
  getCollateralAssets,
} from "../../../utils/asset/assets";
import {
  DepositAssetDropdownItem,
  DepositAssetDropdownTitle,
} from "../../DepositWithdrawModal/style";
import SelectMultichainNetwork from "../../SelectMultichainNetwork";
import { Chevron } from "../../shared/Chevron/style";
import Dropdown from "../../shared/Dropdown";

interface DepositInputSectionProps {
  account?: string | null;
  copied: boolean;
  handleCopyAddress: () => void;
  selectedDepositNetwork: ChainIdEnum;
  onChangeSelectedDepositNetwork: (chain: ChainIdEnum) => void;
  assetDropdownOpen: boolean;
  setAssetDropdownOpen: (open: boolean) => void;
  asset: DepositWithdrawCollaterals;
  onChangeAsset: (asset: DepositWithdrawCollaterals) => void;
  isDepositing: boolean;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
  customError?: string;
  maxBalance?: string;
  /** Why no maximum can be offered, when there is none. */
  maxUnavailableReason?: string;
  balance?: string;
  decimals: number;
  onMaxClick: () => void;
  sliderPercent: number;
  onSliderChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function DepositInputSection({
  account,
  copied,
  handleCopyAddress,
  selectedDepositNetwork,
  onChangeSelectedDepositNetwork,
  assetDropdownOpen,
  setAssetDropdownOpen,
  asset,
  onChangeAsset,
  isDepositing,
  register,
  errors,
  customError,
  maxBalance,
  maxUnavailableReason,
  balance,
  decimals,
  onMaxClick,
  sliderPercent,
  onSliderChange,
}: DepositInputSectionProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "ConnectWalletModal.Deposit",
  });
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });
  const { t: formError } = useTranslation("formErrors");
  return (
    <InputContainer>
        {account && (
          <QRSection>
            <InputLabel>{t("deposit_address")}</InputLabel>
            <QRWrapper>
              <QRCodeSVG value={account} size={120} />
            </QRWrapper>
            <AddressText>{shortenAddress(account)}</AddressText>
            <CopyButton type="button" onClick={handleCopyAddress}>
              {copied ? t("copied") : t("copy_address")}
            </CopyButton>
          </QRSection>
        )}
          <InputLabel>{t("deposit_from")}</InputLabel>
          <SelectMultichainNetwork
            selectedChain={selectedDepositNetwork}
            onSelectChain={onChangeSelectedDepositNetwork}
            />

          <InputLabel>{t("amount")}</InputLabel>
          <Input
            placeholder="0.00"
            leftAccessory={
              <Dropdown
                onToggle={setAssetDropdownOpen}
                toggleStyles={{
                  padding: SPACING.one,
                  backgroundColor: FIELD_COLORS.two,
                }}
                dropdownMenuContainerStyles={{
                  overflow: "hidden",
                  backgroundColor: FIELD_COLORS.two,
                }}
                title={
                  <DepositAssetDropdownTitle>
                    <img
                      src={getAssetLogo(asset)}
                      width={24}
                      height={24}
                      alt={asset}
                    />
                    <Chevron
                      size="small"
                      direction={assetDropdownOpen ? "up" : "down"}
                    />
                  </DepositAssetDropdownTitle>
                }
                items={getCollateralAssets(
                  selectedDepositNetwork,
                  "deposit"
                ).map((v) => ({
                  label: (
                    <DepositAssetDropdownItem selected={v === asset}>
                      <img
                        src={getAssetLogo(v)}
                        width={24}
                        height={24}
                        alt={v}
                      />
                      <span>
                        {getAssetShortName(v, selectedDepositNetwork)}
                      </span>
                    </DepositAssetDropdownItem>
                  ),
                  onSelect: () => {
                    onChangeAsset(v as DepositWithdrawCollaterals);
                  },
                }))}
              />
            }
            rightAccessory={
              <MaxButton
                onClick={onMaxClick}
                type="button"
                disabled={maxBalance === undefined}
                title={
                  maxUnavailableReason
                    ? inputsT(`max_unavailable.${maxUnavailableReason}`)
                    : undefined
                }
              >
                {inputsT("max")}
              </MaxButton>
            }
            type="number"
            disabled={isDepositing}
            {...register("amount", {
              required: true,
              // Compared in base units: `parseFloat` let exponent text and too
              // many decimals through to `parseUnits`, which then threw.
              validate: {
                validDecimal: (v) => isValidDecimalInput(v, decimals),
                moreThanZero: (v) => Boolean(toTokenUnits(v, decimals)?.gt(0)),
                lessThanEqualToBalance: (v) => {
                  const amount = toTokenUnits(v, decimals);
                  // The gas-reserved maximum, never the raw wallet balance.
                  const max = toTokenUnits(maxBalance ?? "0", decimals);
                  return Boolean(amount && max && amount.lte(max));
                },
              },
            })}
            error={Boolean(Object.keys(errors).length)}
          />
          {errors.amount?.type === "required" && (
            <ExtrasText $type="error">{formError("amount.required")}</ExtrasText>
          )}
          {errors.amount?.type === "validDecimal" && (
            <ExtrasText $type="error">
              {invalidAmountMessage(formError, decimals)}
            </ExtrasText>
          )}
          {errors.amount?.type === "moreThanZero" && (
            <ExtrasText $type="error">
              {formError("amount.moreThanZero")}
            </ExtrasText>
          )}
          {errors.amount?.type === "lessThanEqualToBalance" && (
            <ExtrasText $type="error">
              {formError("amount.lessThanEqualToBalance")}
            </ExtrasText>
          )}
          {Boolean(!errors.amount?.type && customError) && (
            <ExtrasText $type="error">{customError}</ExtrasText>
          )}
          <SliderContainer>
            <SliderRow>
              <SliderInput
                type="range"
                min={0}
                max={100}
                step={1}
                value={sliderPercent}
                percentage={sliderPercent}
                onChange={onSliderChange}
                disabled={isDepositing}
              />
              <PercentageLabel>{sliderPercent}%</PercentageLabel>
            </SliderRow>
          </SliderContainer>
        </InputContainer>
  );
}

export default DepositInputSection;
